import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type Document, type SubmissionSibling } from '@/api/v2Client';
import { listAllSubmissionDocuments } from '@/lib/listAllSubmissionDocuments';
import { sectionKey } from '@/lib/sectionKey';
import { docReviewFlags } from '@/lib/docReviewFlags';
import { SectionTree, type SectionTreeDocLeaf, type SectionTreeProps } from './SectionTree';

interface Props extends Omit<SectionTreeProps, 'documentsByKey' | 'unplacedDocuments'> {
  readonly submissionId: string;
  readonly sequenceNumber: string | number | null;
  readonly sequenceDescription?: string | null;
  readonly documents: readonly Document[];
  readonly documentsLoading?: boolean;
  readonly documentsError?: string | null;
  readonly onRetryDocuments?: () => void;
  readonly onOpenSequenceDocument: (submissionId: string, documentId: string) => void;
}

type SequenceDocument = Document & SectionTreeDocLeaf;
const buttonStyle: CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 4, padding: '3px 8px',
  background: 'var(--surface)', color: 'var(--text)', fontSize: 11, cursor: 'pointer',
};
const sequenceRowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, padding: '4px 0', minWidth: 0 };


/** One application tree; sequence identity belongs to document leaves. */
export function ApplicationSectionTree({
  submissionId, sequenceNumber, sequenceDescription, documents, documentsLoading, documentsError, onRetryDocuments, onOpenSequenceDocument, ...treeProps
}: Props): ReactElement {
  const [siblings, setSiblings] = useState<readonly SubmissionSibling[]>([]);
  const [familyLoading, setFamilyLoading] = useState(true);
  const [familyError, setFamilyError] = useState(false);
  const [retry, setRetry] = useState(0);
  // null means all sequences, including any newly discovered sequence.
  const [selection, setSelection] = useState<ReadonlySet<string> | null>(null);
  const [loaded, setLoaded] = useState<ReadonlyMap<string, readonly Document[]>>(new Map());
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const currentId = submissionId.toLowerCase();

  useEffect(() => {
    let cancelled = false;
    setFamilyLoading(true);
    setFamilyError(false);
    v2.submissions.getSiblings(submissionId).then((rows) => {
      if (!cancelled) setSiblings(rows);
    }).catch(() => {
      if (!cancelled) setFamilyError(true);
    }).finally(() => {
      if (!cancelled) setFamilyLoading(false);
    });
    return () => { cancelled = true; };
  }, [submissionId, retry]);

  const otherSequences = useMemo(() => siblings
    .filter((s) => s.Id.toLowerCase() !== currentId)
    .sort((a, b) => Number(a.SequenceNumber) - Number(b.SequenceNumber)), [siblings, currentId]);
  const selectedOthers = useMemo(() => otherSequences.filter((s) => selection === null || selection.has(s.Id)), [otherSequences, selection]);
  const showCurrent = selection === null || selection.has(submissionId);
  const browsingOtherSequences = selectedOthers.length > 0;

  useEffect(() => {
    let cancelled = false;
    setLoaded(new Map());
    setFailed(new Set());
    for (const sibling of selectedOthers) {
      void listAllSubmissionDocuments(sibling.Id).then((rows) => {
        if (!cancelled) setLoaded((prev) => new Map(prev).set(sibling.Id, rows));
      }).catch(() => {
        if (!cancelled) setFailed((prev) => new Set(prev).add(sibling.Id));
      });
    }
    return () => { cancelled = true; };
  }, [selectedOthers, retry]);

  const visibleDocuments = useMemo((): SequenceDocument[] => {
    const currentSequence = sequenceNumber ?? siblings.find((s) => s.Id.toLowerCase() === currentId)?.SequenceNumber ?? null;
    const groups = [
      ...(showCurrent ? [{ id: submissionId, sequence: currentSequence, docs: documents }] : []),
      ...selectedOthers.map((s) => ({ id: s.Id, sequence: s.SequenceNumber, docs: loaded.get(s.Id) ?? [] })),
    ].sort((a, b) => Number(a.sequence) - Number(b.sequence));
    return groups.flatMap((group) => group.docs.map((doc) => ({
      ...doc,
      SubmissionId: group.id,
      SequenceNumber: group.sequence == null ? null : String(group.sequence).padStart(4, '0'),
    })));
  }, [sequenceNumber, siblings, currentId, showCurrent, submissionId, documents, selectedOthers, loaded]);

  const buckets = useMemo(() => {
    const byKey = new Map<string, SequenceDocument[]>();
    const unplaced: SequenceDocument[] = [];
    const unconfirmed = new Set<string>();
    const lowConfidence = new Set<string>();
    const knownKeys = new Set<string>();
    const visit = (nodes: SectionTreeProps['tree']): void => {
      for (const node of nodes) { knownKeys.add(sectionKey(node.code)); visit(node.children); }
    };
    visit(treeProps.tree);
    for (const doc of visibleDocuments) {
      const key = sectionKey(doc.CtdSectionCode ?? doc.Classification ?? '');
      // Keep documents from older regional trees reachable even if their section
      // is absent from the current specification's tree.
      if (!key || !knownKeys.has(key)) unplaced.push(doc);
      else byKey.set(key, [...(byKey.get(key) ?? []), doc]);
      const flags = docReviewFlags(doc);
      if (flags.unconfirmed) unconfirmed.add(key);
      if (flags.lowConfidence) lowConfidence.add(key);
    }
    return { byKey, unplaced, unconfirmed, lowConfidence };
  }, [visibleDocuments, treeProps.tree]);

  const sequenceByDocumentId = useMemo(() => {
    const result = new Map<string, string>();
    for (const [id, sequence] of treeProps.priorSeqByDocId ?? []) result.set(id.toLowerCase(), sequence);
    for (const doc of visibleDocuments) {
      if (doc.SequenceNumber != null) result.set(doc.Id.toLowerCase(), doc.SequenceNumber);
    }
    return result;
  }, [treeProps.priorSeqByDocId, visibleDocuments]);
  const currentDescription = sequenceDescription?.trim()
    || siblings.find((s) => s.Id.toLowerCase() === currentId)?.SequenceDescription?.trim()
    || 'No description';

  const toggle = (id: string): void => {
    setSelection((prev) => {
      const next = new Set(prev ?? [submissionId, ...otherSequences.map((s) => s.Id)]);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const loadingOthers = selectedOthers.some((s) => !loaded.has(s.Id) && !failed.has(s.Id));

  return (
    <>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
        <details data-testid="tree-sequence-selector">
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            Sequences: {selection === null ? 'All sequences' : `${selectedOthers.length + Number(showCurrent)} selected`}
          </summary>
          <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
            <button style={buttonStyle} type="button" onClick={() => setSelection(null)}>All sequences</button>
            <button style={buttonStyle} type="button" onClick={() => setSelection(new Set([submissionId]))}>Current only</button>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          <label style={sequenceRowStyle} title={currentDescription}>
            <input type="checkbox" aria-label={`${sequenceNumber == null ? '—' : String(sequenceNumber).padStart(4, '0')} (current) — ${currentDescription}`} checked={showCurrent} onChange={() => toggle(submissionId)} />
            <span style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{sequenceNumber == null ? '—' : String(sequenceNumber).padStart(4, '0')}</span>
            <span style={{ color: 'var(--text-muted)' }}>(current)</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{currentDescription}</span>
          </label>
          {otherSequences.map((s) => (
            <label key={s.Id} style={sequenceRowStyle} title={s.SequenceDescription?.trim() || 'No description'}>
              <input type="checkbox" aria-label={`${String(s.SequenceNumber).padStart(4, '0')} — ${s.SequenceDescription?.trim() || 'No description'}`} checked={selection === null || selection.has(s.Id)} onChange={() => toggle(s.Id)} />{' '}
              <span style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{String(s.SequenceNumber).padStart(4, '0')}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{s.SequenceDescription?.trim() || 'No description'}</span>
            </label>
          ))}
          </div>
        </details>
        <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
          {visibleDocuments.length} document{visibleDocuments.length === 1 ? '' : 's'} shown{familyLoading || loadingOthers || documentsLoading ? ' · Loading sequences…' : ''}
        </div>
        {documentsError && <div role="alert">{documentsError} <button style={buttonStyle} type="button" onClick={onRetryDocuments}>Retry documents</button></div>}
        {familyError && <div role="alert">Could not load application sequences. <button style={buttonStyle} type="button" onClick={() => setRetry((n) => n + 1)}>Retry</button></div>}
        {selectedOthers.filter((s) => failed.has(s.Id)).map((s) => (
          <div role="alert" key={s.Id}>Could not load documents for sequence {String(s.SequenceNumber).padStart(4, '0')}. <button style={buttonStyle} type="button" onClick={() => setRetry((n) => n + 1)}>Retry</button></div>
        ))}
        {browsingOtherSequences && <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>Select a document to open its sequence. Choose Current only to arrange documents.</div>}
      </div>
      <SectionTree
        {...treeProps}
        priorSeqByDocId={sequenceByDocumentId}
        documentsByKey={buckets.byKey}
        unplacedDocuments={buckets.unplaced}
        unconfirmedCodes={buckets.unconfirmed}
        lowConfidenceCodes={buckets.lowConfidence}
        onSelectDocument={(documentId) => {
          const doc = visibleDocuments.find((d) => d.Id === documentId);
          if (doc && doc.SubmissionId.toLowerCase() !== currentId) onOpenSequenceDocument(doc.SubmissionId, doc.Id);
          else treeProps.onSelectDocument?.(documentId);
        }}
        {...(browsingOtherSequences || !showCurrent ? { onMoveDocument: undefined, onDropDocument: undefined, onDropFiles: undefined } : {})}
      />
    </>
  );
}
